import { useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { AddItemIcon } from './AddItemIcon';

export type ProfileArtifactFileDropzoneProps = {
  inputId: string;
  accept: string;
  ariaLabel: string;
  /** Shown in dropzone; parent passes busy vs idle strings when needed */
  primaryLabel: ReactNode;
  disabled?: boolean;
  busy?: boolean;
  multiple?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

export default function ProfileArtifactFileDropzone({
  inputId,
  accept,
  ariaLabel,
  primaryLabel,
  disabled = false,
  busy = false,
  multiple = false,
  onChange,
}: ProfileArtifactFileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const isDisabled = disabled || busy;

  const emitFiles = (files: FileList | File[]) => {
    const input = inputRef.current;
    if (isDisabled || !input) {
      return;
    }

    const picked = Array.from(files);
    if (picked.length === 0) {
      return;
    }

    const dataTransfer = new DataTransfer();
    const toAssign = multiple ? picked : picked.slice(0, 1);
    toAssign.forEach(file => dataTransfer.items.add(file));
    input.files = dataTransfer.files;

    onChange({
      target: input,
      currentTarget: input,
    } as ChangeEvent<HTMLInputElement>);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    emitFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!isDisabled) {
      setDragging(true);
    }
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  return (
    <div className="company-form__import-actions">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        className="visually-hidden"
        accept={accept}
        multiple={multiple}
        disabled={isDisabled}
        aria-label={ariaLabel}
        onChange={onChange}
      />
      <button
        type="button"
        className={`dropzone${dragging ? ' dragging' : ''}${isDisabled ? ' disabled' : ''}`}
        disabled={isDisabled}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="dropzone-icon" aria-hidden="true">
          <AddItemIcon />
        </div>
        <div className="dropzone-text">
          <span className="dropzone-primary">{primaryLabel}</span>
        </div>
      </button>
    </div>
  );
}
